import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthContext.jsx';
import { openGlobalOverlay, closeGlobalOverlay } from '../util/globalOverlays.js';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { useNavigate } from 'react-router-dom';
import { api } from '../api.js';
import { toast } from './Toasts.jsx';
import { TRACKER_STATUSES } from '@hunter/shared/applications';
import { navCommands } from '../nav.js';

// T074 — Global Cmd+K / Ctrl+K command palette.
//
// Two-tier surface: hard-coded navigation/action commands always show, and
// live recommendations are pulled lazily each time the palette opens.
//
// T290 — it used to fetch ONCE per browser session. The palette is mounted in
// App.jsx outside <Routes>, so it never unmounts: one GET per page-load, ever.
// The comment here claimed the cache was "refreshed manually via the 'Refresh
// recs' command" — there is no such command, and never was.
//
// The cost was a dead click, which is the exact thing T230 was written to fix:
// open the palette at 09:00 (job X is `new`), mark X applied at 09:20, search
// for X at 09:30 — the snapshot still says `new`, so it routes to the dashboard
// instead of the tracker, the dashboard filters applied rows out of the list,
// and the operator gets "That job is not in the current view". The same cache
// also hid every job a scan added after the first open, and survived a
// logout/login with the previous account's titles in it.
//
// A stale-while-revalidate open: what is already loaded renders immediately, and
// the fetch replaces it. 80 rows is one small request against a route the
// dashboard already calls. Fuzzy match is a small in-house scorer:
// substring + token-prefix + initials. We deliberately don't ship Fuse.js
// — the rec list is bounded (~100) and the navigation list is < 20, so a
// 30-line scorer outperforms any dependency import on cold-cache fetch.
//
// Mounted ONCE near the root (App.jsx). Keyboard shortcut is global; on
// macOS Cmd+K, elsewhere Ctrl+K. Esc + outside-click + Enter close it.

// T183 — the `kbd: 'g d'` labels used to advertise chord shortcuts that were
// never implemented anywhere; an advertised shortcut that does nothing is worse
// than none, so they are gone until someone builds them. Analytics + Sources
// were also missing, leaving 2 of 8 routes unreachable from the palette.
const NAV_COMMANDS = navCommands(); // T271(2) — derived from nav.js

// Exposed so a test can assert every route has a way in — twice now a page
// has shipped without one (T183, then T265 for /digest).
export const NAV_COMMANDS_FOR_TEST = NAV_COMMANDS;

// T230 — statuses the Dashboard deliberately hides (it shows the triage queue,
// not the tracker). A palette hit on one of these must go to /applications, or
// selecting it lands on a page that will never render it.
// T271(1) — was a hand-written copy of the same six keys.


// Actions that hit endpoints directly — quick triggers without leaving the
// page. Each handler returns a Promise (or void); the palette closes on
// success.
const ACTION_FACTORY = (navigate) => [
  {
    id: 'scan-now',
    label: 'Scan now (re-run daily scan)',
    hint: 'POST /scans/trigger — ~30–60s across all adapters',
    section: 'Actions',
    kind: 'action',
    keywords: ['refresh', 'jobs', 'sources'],
    run: async () => {
      // T101/T147 — scans are async (202 + poll). The palette fires it and
      // acknowledges; the dashboard ScanPanel shows live progress if open.
      const r = await api.post('/scans/trigger', {});
      toast(r.alreadyRunning ? 'A scan is already running.' : 'Scan started — fanning out across sources…', { kind: 'info' });
    },
  },
  {
    id: 'imap-scan',
    label: 'Pull LinkedIn job-alert emails from inbox',
    hint: 'Requires email inbox connected in Settings → Connectors',
    section: 'Actions',
    kind: 'action',
    keywords: ['email', 'mail', 'linkedin'],
    run: async () => {
      const r = await api.post('/scans/imap-linkedin', {});
      toast(r.alreadyRunning ? 'An inbox scan is already running.' : 'Inbox scan started — pulling LinkedIn alerts…', { kind: 'info' });
    },
  },
  {
    id: 'open-manual-paste',
    label: 'Paste a job (URL or text)',
    hint: 'Quick way to add a one-off recommendation',
    section: 'Actions',
    kind: 'action',
    keywords: ['add', 'manual', 'paste'],
    run: () => {
      navigate('/');
      // The dashboard owns the modal; fire a CustomEvent it can listen to.
      window.dispatchEvent(new CustomEvent('hunter:open-manual-job'));
    },
  },
  {
    // T421 — the other half of a pair that was only ever built once.
    //
    // The dashboard has listened for `hunter:open-import-jobs` since the
    // palette was written, and nothing has ever dispatched it. The UX
    // inventory read that as dead wood to delete; it is the opposite — the
    // listener's two siblings (`open-manual-job`, `focus-rec`) both have
    // dispatchers here, so this is the one command that was forgotten.
    //
    // Worth finishing rather than removing: import is where 53% of his
    // applications come from over the last four weeks, which makes it the
    // single most-used entry point in the product and the one most worth
    // reaching without the mouse.
    id: 'open-import-jobs',
    label: 'Import jobs from links',
    hint: 'Paste one or more job URLs',
    section: 'Actions',
    kind: 'action',
    keywords: ['import', 'links', 'urls', 'bulk'],
    run: () => {
      navigate('/');
      window.dispatchEvent(new CustomEvent('hunter:open-import-jobs'));
    },
  },
];

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  // T290 — this component is mounted unconditionally in App.jsx, so unlike every
  // page it does NOT unmount on logout and its rows outlive the account that
  // loaded them. The open handler refetches, but stale-while-revalidate means the
  // previous account's job titles paint first. Clearing on identity change is the
  // only thing that makes the "survived a logout/login" note above actually true.
  const { user } = useAuth();
  const [recs, setRecs] = useState([]);
  // `recsLoaded` distinguishes "never fetched" from "fetched and empty", which
  // is what lets the list show nothing rather than a spinner forever. It no
  // longer gates the fetch — that is what made the palette stale for a session.
  const [, setRecsLoaded] = useState(false);
  const [recsLoading, setRecsLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  // T248 — the palette declared `aria-modal="true"` without a trap, so Tab
  // walked out of it into the page underneath. useFocusTrap's own doc names
  // this component ("For always-mounted overlays (CommandPalette, ToastHistory)
  // gate `active` on their open boolean") — gating on `open` is what makes the
  // focus RESTORE fire on close rather than on unmount, which never happens
  // here. The input keeps initial focus.
  const panelRef = useRef(null);
  const listRef = useRef(null);
  const navigate = useNavigate();

  // Global keyboard hotkey — Cmd/Ctrl + K. Also listen for a CustomEvent
  // so the sidebar "Quick jump" button can open the palette without
  // synthesizing a keyboard event (which Chrome/Safari don't honor).
  useEffect(() => {
    const onKey = (e) => {
      const isOpen = (e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey);
      if (isOpen) {
        e.preventDefault();
        setOpen((cur) => !cur);
      } else if (e.key === 'Escape' && open) {
        e.preventDefault();
        setOpen(false);
      }
    };
    const onOpenEvent = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('hunter:open-palette', onOpenEvent);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('hunter:open-palette', onOpenEvent);
    };
  }, [open]);

  // Reset state on open, focus input, lazy-load recs.
  // T290b — only a CHANGE between two known identities clears the rows. The
  // first pass fired on `[user?.id]` outright, which includes the initial
  // undefined → id transition when the provider's bootstrap resolves. That
  // lands after the palette may already have fetched, so opening ⌘K during the
  // first seconds of a page load wiped the rows it had just loaded and nothing
  // refetched them until the next open.
  const lastUserIdRef = useRef(undefined);
  useEffect(() => {
    const prev = lastUserIdRef.current;
    lastUserIdRef.current = user?.id;
    if (prev === undefined || prev === user?.id) return;
    setRecs([]);
    setRecsLoaded(false);
  }, [user?.id]);

  // T290b — announce openness to pages that render their own overlays. This
  // component is mounted at the App root, so a page's Escape gate has no other
  // way to know the palette is sitting above it. See util/globalOverlays.js.
  useEffect(() => {
    if (!open) return undefined;
    openGlobalOverlay('command-palette');
    return () => closeGlobalOverlay('command-palette');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActiveIndex(0);
    // setTimeout so the input is mounted before focus.
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    if (!recsLoading) {
      setRecsLoading(true);
      api.get('/recommendations?limit=80')
        .then((body) => {
          setRecs(body.recommendations ?? []);
          setRecsLoaded(true);
        })
        .catch(() => {
          // Quietly degrade — nav + actions still work, and whatever was loaded
          // before stays on screen rather than emptying under the cursor.
          setRecsLoaded(true);
        })
        .finally(() => setRecsLoading(false));
    }
    return () => clearTimeout(t);
    // NOT `recsLoaded`: that is what made this fire once and never again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const actionCommands = useMemo(() => ACTION_FACTORY(navigate), [navigate]);

  // Build the candidate set per query.
  const items = useMemo(() => {
    const q = query.trim().toLowerCase();
    const all = [
      ...NAV_COMMANDS.map((c) => ({
        ...c,
        searchable: `${c.label} ${(c.keywords ?? []).join(' ')}`.toLowerCase(),
      })),
      ...actionCommands.map((c) => ({
        ...c,
        searchable: `${c.label} ${(c.keywords ?? []).join(' ')}`.toLowerCase(),
      })),
      ...recs.map((r) => ({
        id: `rec-${r.id}`,
        label: r.job?.title ?? '(no title)',
        sublabel:
          `${r.job?.company ?? '(no company)'} · score ${r.scoreTotal ?? 0}` +
          (TRACKER_STATUSES.has(r.status) ? ` · ${r.status}` : ''),
        section: 'Recommendations',
        kind: 'rec',
        score: r.scoreTotal,
        status: r.status,
        searchable: `${r.job?.title ?? ''} ${r.job?.company ?? ''} ${(r.job?.extracted?.skills ?? []).join(' ')}`.toLowerCase(),
        rec: r,
      })),
    ];
    if (!q) return all.slice(0, 80);
    return all
      .map((c) => ({ c, score: fuzzyScore(c.searchable, q) }))
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 30)
      .map((x) => x.c);
  }, [query, recs, actionCommands]);

  useFocusTrap(panelRef, open, inputRef);

  // Bring active item into view on arrow-nav.
  useEffect(() => {
    if (!open) return;
    setActiveIndex((i) => Math.min(Math.max(0, i), items.length - 1));
  }, [items.length, open]);
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector(`[data-idx="${activeIndex}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);

  const runItem = async (item) => {
    if (!item) return;
    setOpen(false);
    if (item.kind === 'nav') {
      navigate(item.to);
    } else if (item.kind === 'action') {
      try {
        await item.run?.();
      } catch (e) {
        toast(e?.body?.message || e?.message || 'Action failed', { kind: 'error' });
      }
    } else if (item.kind === 'rec') {
      // T230 — route by status. This used to always navigate to the Dashboard
      // and dispatch focus-rec, but the Dashboard filters out every
      // applied-family status, so `getElementById` found nothing and the click
      // silently did nothing at all. Anything in the tracker goes to
      // /applications with the closed stages shown when it is closed.
      if (TRACKER_STATUSES.has(item.status)) {
        const closed = item.status === 'rejected' || item.status === 'withdrawn';
        // T256(9) — this used to send ?show=closed. `closed` is not one of the
        // three values the Applications filter accepts (active / all / stale),
        // so jumping to a rejected application landed on the default "Active
        // only" view, which by definition cannot contain it. `all` can.
        navigate(`/applications${closed ? '?show=all' : ''}`);
        return;
      }
      navigate(`/#rec-${item.rec.id}`);
      window.dispatchEvent(new CustomEvent('hunter:focus-rec', { detail: { id: item.rec.id } }));
    }
  };

  const onInputKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex((i) => Math.min(items.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex((i) => Math.max(0, i - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); runItem(items[activeIndex]); }
  };

  if (!open) return null;

  // Group items by section for the UI.
  const grouped = items.reduce((acc, item, idx) => {
    (acc[item.section] = acc[item.section] || []).push({ ...item, idx });
    return acc;
  }, {});

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      className="fixed inset-0 z-[60] flex items-start justify-center bg-slate-950/60 pt-24 backdrop-blur-sm"
      onClick={() => setOpen(false)}
    >
      <div
        ref={panelRef}
        className="w-full max-w-xl rounded-xl border border-slate-700 bg-slate-900 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-slate-800 px-4 py-3">
          <span className="text-slate-500">⌘</span>
          <input
            ref={inputRef}
            role="combobox"
            aria-expanded="true"
            aria-controls="cmdk-results"
            aria-activedescendant={`cmdk-opt-${activeIndex}`}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActiveIndex(0); }}
            onKeyDown={onInputKey}
            placeholder="Jump to a rec, run an action, or navigate…"
            className="flex-1 bg-transparent text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none"
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className="rounded border border-slate-700 px-1.5 py-0.5 text-[10px] text-slate-400">esc</kbd>
        </div>

        <div ref={listRef} id="cmdk-results" className="max-h-[60vh] overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-4 py-6 text-center text-xs text-slate-500">
              {recsLoading ? 'loading recommendations…' : 'no matches — try a different word'}
            </div>
          ) : (
            Object.entries(grouped).map(([section, group]) => (
              <div key={section}>
                <p className="border-b border-slate-800/60 px-4 pb-1 pt-3 text-[10px] uppercase tracking-widest text-slate-600">
                  {section}
                </p>
                <ul role="listbox" aria-label={section}>
                  {group.map((item) => (
                    <li key={item.id} data-idx={item.idx} role="option" aria-selected={item.idx === activeIndex} id={`cmdk-opt-${item.idx}`}>
                      <button
                        type="button"
                        tabIndex={-1}
                        onClick={() => runItem(item)}
                        onMouseEnter={() => setActiveIndex(item.idx)}
                        className={
                          'flex w-full items-center justify-between gap-3 px-4 py-2 text-left text-sm ' +
                          (item.idx === activeIndex ? 'bg-brand-600/15 text-brand-100' : 'text-slate-200 hover:bg-slate-800/60')
                        }
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate">{item.label}</p>
                          {(item.sublabel || item.hint) && (
                            <p className="truncate text-[10px] text-slate-500">{item.sublabel || item.hint}</p>
                          )}
                        </div>
                        {item.kbd && (
                          <kbd className="shrink-0 rounded border border-slate-700 bg-slate-950/40 px-1.5 py-0.5 font-mono text-[10px] text-slate-400">
                            {item.kbd}
                          </kbd>
                        )}
                        {item.kind === 'rec' && (
                          <span className="shrink-0 font-mono text-[10px] text-slate-400">
                            {item.score}
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-slate-800 px-4 py-2 text-[10px] text-slate-500">
          <div className="flex items-center gap-3">
            <span><kbd className="rounded border border-slate-700 px-1 text-slate-400">↑↓</kbd> navigate</span>
            <span><kbd className="rounded border border-slate-700 px-1 text-slate-400">↵</kbd> run</span>
            <span><kbd className="rounded border border-slate-700 px-1 text-slate-400">esc</kbd> close</span>
          </div>
          <span>{items.length} item{items.length === 1 ? '' : 's'}</span>
        </div>
      </div>
    </div>
  );
}

// fuzzyScore — 30-line in-house ranker. Strong substring > token prefix >
// initials match. Returns 0 if no signal so the caller can filter.
function fuzzyScore(haystack, needle) {
  if (!needle) return 1;
  if (!haystack) return 0;
  if (haystack.includes(needle)) {
    // Earlier match wins (so a job titled "Designer" outranks "Cloud Eng"
    // when the operator types "des").
    const at = haystack.indexOf(needle);
    return 1000 - at + needle.length * 5;
  }
  const tokens = haystack.split(/\s+/);
  let score = 0;
  for (const t of needle.split(/\s+/).filter(Boolean)) {
    let bestForT = 0;
    for (const h of tokens) {
      if (h.startsWith(t)) bestForT = Math.max(bestForT, 200 + t.length * 3);
      else if (h.includes(t)) bestForT = Math.max(bestForT, 80 + t.length);
    }
    if (bestForT === 0) return 0; // every needle token must have a home
    score += bestForT;
  }
  return score;
}
